import React, { FC, useMemo, useState } from 'react';
import { t } from 'i18next';
import { Button } from '@studykik/ui-library';
import { toast } from 'react-toastify';
import {
  Available,
  BalanceContainer,
  BalanceLabel,
  ClaimSummaryWrapper,
  DataContainer,
  Pending,
} from './styles';
import formatCurrency from '../../utils/formatCurrency';
import {
  PaymentSubmission,
  SubmissionApprovedStatus,
  SubmissionInProgressStatus,
} from '../../models/paymentSubmissions';
import processPatientRequests from '../../services/paymentSubmissions/processPatientRequests';

export interface IClaimSummary {
  paymentSubmissions: PaymentSubmission[];
  patientId: string;
  fetchData: () => Promise<void>;
}

// Exported for testing
export const handleClaim = async ({
  patientId,
  fetchData,
  setIsProcessing,
  currency,
}: {
  patientId: string;
  fetchData: () => Promise<void>;
  setIsProcessing: (status: false | string) => void;
  currency: string;
}) => {
  try {
    setIsProcessing(currency);
    const url = await processPatientRequests(patientId, currency);
    window.open(url);
    fetchData();
  } catch (err) {
    toast.error(t('Failed'));
  } finally {
    setIsProcessing(false);
  }
};

export const ClaimSummary: FC<IClaimSummary> = (props) => {
  const { paymentSubmissions = [], patientId, fetchData } = props;

  const [isProcessing, setIsProcessing] = useState<false | string>(false);

  const balances = useMemo(
    () =>
      paymentSubmissions.reduce<{
        [currencyCode: string]: { available: number; pending: number };
      }>((acc, submission) => {
        acc[submission.currency] = acc[submission.currency] || {
          available: 0,
          pending: 0,
        };

        if (submission.status === SubmissionApprovedStatus) {
          acc[submission.currency].available += Number(submission.amount);
        }

        if (SubmissionInProgressStatus.includes(submission.status)) {
          acc[submission.currency].pending += Number(submission.amount);
        }

        return acc;
      }, {}),
    [paymentSubmissions]
  );

  return !Object.keys(balances).length ? (
    <ClaimSummaryWrapper>
      <DataContainer>
        <BalanceLabel>{t('Balance')}</BalanceLabel>
        <BalanceContainer>
          <Available>$0.00 USD</Available>
          <Pending>{`$0 ${t('is pending')}`}</Pending>
        </BalanceContainer>
      </DataContainer>
      <Button variant="outlined" disabled className="claim-funds-button">
        {t('Claim Now')}
      </Button>
    </ClaimSummaryWrapper>
  ) : (
    <>
      {Object.entries(balances).map(([currency, balance]) => (
        <ClaimSummaryWrapper key={currency}>
          <DataContainer>
            <BalanceLabel>{t('Balance')}</BalanceLabel>
            <BalanceContainer>
              <Available>
                {formatCurrency({ amount: balance.available, currency })}{' '}
                {currency}
              </Available>
              <Pending>
                {`${formatCurrency({ amount: balance.pending, currency })} ${t(
                  'is pending'
                )}`}
              </Pending>
            </BalanceContainer>
          </DataContainer>
          <Button
            onClick={() => {
              handleClaim({
                patientId,
                fetchData,
                setIsProcessing,
                currency,
              });
            }}
            variant="outlined"
            disabled={!balance.available}
            isLoading={isProcessing === currency}
            className="claim-funds-button"
          >
            {t('Claim Now')}
          </Button>
        </ClaimSummaryWrapper>
      ))}
    </>
  );
};
